// Shared HTTP plumbing and venue-adapter contract. Extracted from the
// venues-common package — only the helpers referenced by the Polymarket
// adapter (fetchJson, url, VenueAdapter interface, market-id helper) are
// kept.

import type {
  Market,
  MarketFilters,
  OrderBook,
  OrderRequest,
  OrderResult,
  OrderStatus,
  Position,
  Venue,
} from "../types";

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------

export type FetchOpts = {
  /** Abort after this many ms. Default 8s. */
  timeoutMs?: number;
  /** Headers merged onto the request. */
  headers?: Record<string, string>;
  /** Use Cloudflare's edge cache for the URL. Default true. */
  edgeCache?: boolean | { ttlSeconds: number };
  /** Retry the request once on network error. Default false. */
  retry?: boolean;
};

export class VenueHttpError extends Error {
  readonly status: number;
  readonly url: string;
  readonly bodyPreview?: string;
  constructor(url: string, status: number, bodyPreview?: string) {
    super(`HTTP ${status} for ${url}`);
    this.name = "VenueHttpError";
    this.url = url;
    this.status = status;
    this.bodyPreview = bodyPreview;
  }
}

const DEFAULT_TIMEOUT = 8_000;
const DEFAULT_CACHE_TTL = 60;

/**
 * Fetch JSON with timeout, optional Cloudflare edge cache, and a typed error
 * shape. Returns the parsed body on 2xx, throws VenueHttpError otherwise.
 */
export async function fetchJson<T>(url: string, opts: FetchOpts = {}): Promise<T> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? DEFAULT_TIMEOUT);

  const init: RequestInit = {
    signal: ctrl.signal,
    headers: { accept: "application/json", ...(opts.headers ?? {}) },
  };
  const cache = opts.edgeCache ?? true;
  if (cache) {
    const ttl = typeof cache === "object" ? cache.ttlSeconds : DEFAULT_CACHE_TTL;
    (init as RequestInit & { cf?: unknown }).cf = {
      cacheEverything: true,
      cacheTtl: ttl,
    };
  }

  try {
    const res = await fetch(url, init);
    if (!res.ok) {
      const text = await safeText(res);
      throw new VenueHttpError(url, res.status, text.slice(0, 200));
    }
    const ct = res.headers.get("content-type") ?? "";
    if (!ct.includes("json")) {
      const text = await safeText(res);
      throw new VenueHttpError(url, res.status, `non-json: ${text.slice(0, 200)}`);
    }
    return (await res.json()) as T;
  } catch (e) {
    if (opts.retry && !(e instanceof VenueHttpError)) {
      // One soft retry on network/abort errors only.
      return fetchJson<T>(url, { ...opts, retry: false });
    }
    throw e;
  } finally {
    clearTimeout(t);
  }
}

async function safeText(res: Response): Promise<string> {
  try {
    return await res.text();
  } catch {
    return "";
  }
}

/**
 * Build a URL by merging path + query params, dropping null/undefined entries.
 */
export function url(base: string, path: string, query?: Record<string, unknown>): string {
  const u = new URL(path, base.endsWith("/") ? base : base + "/");
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      u.searchParams.set(k, String(v));
    }
  }
  return u.toString();
}

// ---------------------------------------------------------------------------
// VenueAdapter contract
// ---------------------------------------------------------------------------

export type Unsubscribe = () => void;

export type VenueMarketsAdapter = {
  search(query: string, filters?: MarketFilters, limit?: number): Promise<Market[]>;
  get(venueMarketId: string): Promise<Market | null>;
  list(filters?: MarketFilters, limit?: number): Promise<Market[]>;
};

export type VenueDepthAdapter = {
  fetchBook(tokenId: string): Promise<OrderBook>;
  subscribeBook(tokenId: string, cb: (book: OrderBook) => void): Unsubscribe;
};

export type VenueAccountAdapter = {
  fetchPositions(address?: string): Promise<Position[]>;
  subscribePositions(address: string, cb: (positions: Position[]) => void): Unsubscribe;
};

export type VenueTradingAdapter = {
  placeOrder(req: OrderRequest): Promise<OrderResult>;
  cancelOrder(orderId: string): Promise<OrderResult>;
  getOrder(orderId: string): Promise<OrderStatus | null>;
};

export type VenueAuthState = {
  ready: boolean;
  address?: string;
  apiKeys?: boolean;
};

export type VenueAuthAdapter = {
  init(secret?: string): Promise<VenueAuthState>;
  state(): VenueAuthState;
  clear(): void;
};

export type VenueAdapter = {
  readonly venue: Venue;

  initialize(): Promise<void>;
  destroy(): void;

  markets: VenueMarketsAdapter;
  depth: VenueDepthAdapter;
  account: VenueAccountAdapter;
  trading: VenueTradingAdapter;
  auth: VenueAuthAdapter;
};

// ---------------------------------------------------------------------------
// Market id helper
// ---------------------------------------------------------------------------

export function toPmapiMarketId(venue: Venue, venueMarketId: string): string {
  return `${venue}:${venueMarketId}`;
}
